// Renders one pack's collection grid. Each cell is the same square size and
// shows one of two states:
//
//   not yet colored — blurred reference lineart, "I roughly know the shape
//                     but haven't earned it" hint. Both unlocked-pending and
//                     fully-empty render this way; InventoryPage handles the
//                     "go color this one" flow separately.
//   colored         — the kid's own artwork from R2.
//
// Replaces the earlier free-form scene that used normalized x/y/w/h slot coords
// {x,y,w,h} normalized coords — those didn't survive dogfood (slots looked
// randomly placed). The schema's scene block is dead-but-kept; cleanup in a
// follow-up so we don't widen the diff here.
//
// Per CLAUDE.md non-negotiable #6, filled cells NEVER show the reference
// colored asset — only the kid's actual paint.

import { motion } from 'framer-motion'
import { Info } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { assetUrl, PACKS, type PackId, type SceneItem } from '@skena/schema'

interface PackSceneProps {
  packId: PackId
  items: readonly SceneItem[]
  /**
   * If set, the matching cell enters with a spring drop-in animation —
   * fired when the kid lands on this page after finishing coloring.
   */
  justCompletedItemId?: string
}

// Mid-blur — kid sees the silhouette enough to anticipate ("ooh, what's
// that?") without giving the actual artwork away. Pair with a slight
// grayscale so the colored ref doesn't leak through the blur.
const LINEART_BLUR_PX = 9

export function PackScene({ packId, items, justCompletedItemId }: PackSceneProps) {
  const allDormant = items.every((i) => !i.isUnlocked && !i.isComplete)
  return (
    <div role="region" aria-label={`${PACKS[packId].nameZh} 蒐集進度`}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {items.map((item) => (
          <SceneSlotView
            key={item.itemId}
            packId={packId}
            item={item}
            flyIn={justCompletedItemId === item.itemId}
          />
        ))}
      </div>
      {allDormant ? (
        <p className="mt-4 text-center text-xs italic text-[var(--color-muted)]">
          先去練習，蒐集第一個元素
        </p>
      ) : null}
    </div>
  )
}

interface SceneSlotViewProps {
  packId: PackId
  item: SceneItem
  flyIn: boolean
}

function SceneSlotView({ packId, item, flyIn }: SceneSlotViewProps) {
  const baseClass =
    'skena-frame group relative aspect-square overflow-hidden rounded-[var(--radius-soft)] bg-white/70 shadow-[var(--shadow-card)]'

  const lineartUrl = assetUrl(packId, item.itemId, 'lineart')

  if (item.isComplete && item.artworkUrl) {
    const initial = flyIn ? { y: -120, opacity: 0, scale: 0.6 } : false
    const animate = { y: 0, opacity: 1, scale: 1 }
    const flyTransition = flyIn
      ? { type: 'spring' as const, stiffness: 180, damping: 18, mass: 0.9 }
      : { type: 'spring' as const, stiffness: 280, damping: 22 }
    // Colored cells navigate back into the coloring page so the kid can
    // keep iterating on their artwork. v0.1 alpha lets completed artwork
    // stay editable — the spec's "complete = final" was a placeholder
    // assumption that didn't survive dogfood.
    return (
      <motion.div
        className={baseClass}
        initial={initial}
        animate={animate}
        whileHover={{ scale: 1.04 }}
        transition={flyTransition}
      >
        <Link
          to={`/pack/${packId}/color/${item.itemId}`}
          className="block h-full w-full focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={`${item.nameZh} — 看／繼續上色`}
        >
          <img
            src={item.artworkUrl}
            alt={item.nameZh}
            crossOrigin="anonymous"
            className="h-full w-full object-cover"
          />
        </Link>
        <InfoIconButton packId={packId} item={item} />
        <SlotLabel tone="colored">{item.nameZh}</SlotLabel>
      </motion.div>
    )
  }

  // Unlocked-but-not-colored: show the lineart clearly + make the whole
  // cell a Link into the coloring page. v0.1 alpha dogfood read: 7-year-old
  // intuition is "see lineart → tap to color"; routing them through the
  // separate inventory page was friction.
  if (item.isUnlocked) {
    return (
      <motion.div
        className={baseClass}
        whileHover={{ scale: 1.03 }}
        transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      >
        <Link
          to={`/pack/${packId}/color/${item.itemId}`}
          className="block h-full w-full focus-visible:outline-2 focus-visible:outline-offset-2"
          aria-label={`${item.nameZh} — 去上色`}
        >
          <img
            src={lineartUrl}
            alt=""
            aria-hidden
            className="h-full w-full object-cover opacity-90"
            onError={(e) => {
              ;(e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        </Link>
        <InfoIconButton packId={packId} item={item} />
        <SlotLabel tone="colored">{item.nameZh}</SlotLabel>
      </motion.div>
    )
  }

  return (
    <motion.div
      className={baseClass}
      transition={{ type: 'spring', stiffness: 280, damping: 22 }}
      aria-label={`${item.nameZh} — 還沒解鎖`}
    >
      <img
        src={lineartUrl}
        alt=""
        aria-hidden
        className="h-full w-full scale-110 object-cover opacity-60"
        style={{ filter: `blur(${LINEART_BLUR_PX}px) grayscale(0.4)` }}
        onError={(e) => {
          // Asset for this item not produced yet — hide silently rather than
          // showing a broken-image icon to a 7-year-old.
          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
        }}
      />
      <SlotLabel tone="dormant">{item.nameZh}</SlotLabel>
    </motion.div>
  )
}


// (i) info button overlay — opens the item detail page. Sits above the
// cell's full-bleed Link, stopPropagation so tapping it doesn't trigger
// the cell's "go color" navigation. Only rendered on unlocked cells.
function InfoIconButton({ packId, item }: { packId: PackId; item: SceneItem }) {
  const navigate = useNavigate()
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        navigate(`/pack/${packId}/item/${item.itemId}`)
      }}
      aria-label={`${item.nameZh} — 看詳情`}
      className="absolute right-1.5 top-1.5 z-10 rounded-full bg-white/85 p-1.5 text-[var(--color-fg)] shadow-sm transition-shadow hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      <Info size={14} aria-hidden />
    </button>
  )
}

function SlotLabel({
  children,
  tone,
}: {
  children: React.ReactNode
  tone: 'colored' | 'dormant'
}) {
  return (
    <span
      className={`absolute inset-x-0 bottom-0 z-10 px-2 py-1.5 text-center text-[11px] font-medium backdrop-blur-sm ${
        tone === 'colored'
          ? 'bg-[var(--color-bg)]/85 text-[var(--color-fg)]'
          : 'bg-[var(--color-bg)]/85 text-[var(--color-muted)]'
      }`}
    >
      {children}
    </span>
  )
}
